import React from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const FIELDS = [
  ['ineKey',           'INE (Clave de Elector)',     {
    minLength: 18,
    maxLength: 18,
    pattern: '[A-Za-z0-9]{18}',
    title: 'La INE debe tener 18 caracteres alfanumericos.',
  }],
  ['curp',             'CURP',                 { maxLength: 18 }],
  ['firstName',        'Nombre(s)',             {}],
  ['lastNamePaternal', 'Apellido Paterno',      {}],
  ['lastNameMaternal', 'Apellido Materno',      {}],
  ['phone',            'Teléfono (10 dígitos)', { inputMode: 'numeric', maxLength: 10 }],
  ['federalDistrict',  'Distrito Federal (1-40)', { inputMode: 'numeric', maxLength: 2, title: 'Ingresa un entero entre 1 y 40.' }],
  ['email',            'Correo Electrónico',   { type: 'email' }],
];

export default function RegisterForm({
  form,
  onChange,
  onSubmit,
  hcaptchaSiteKey = '',
  captchaEnabled = false,
  captchaLoading = false,
  captchaResetCounter = 0,
  onCaptchaVerify,
  onCaptchaExpire,
  onCaptchaError,
  onRefreshCaptcha,
  states = [],
  municipalities = [],
  cees = [],
  catalogLoading = false,
  municipalitiesLoading = false,
  showPassword = false,
  onTogglePassword,
  showConfirmPassword = false,
  onToggleConfirmPassword,
}) {
  function handleChange(key, raw) {
    const val = key === 'phone'
      ? raw.replace(/\D/g, '').slice(0, 10)
      : key === 'federalDistrict'
        ? raw.replace(/\D/g, '').slice(0, 2)
      : key === 'ineKey'
        ? raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 18)
        : key === 'curp'
          ? raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 18)
        : raw;
    onChange({ ...form, [key]: val });
  }

  function handleStateChange(stateIdValue) {
    const selectedState = states.find((item) => String(item.id) === stateIdValue);
    onChange({
      ...form,
      stateId: stateIdValue,
      state: selectedState ? selectedState.name : '',
      municipalityId: '',
      municipality: '',
    });
  }

  function handleMunicipalityChange(municipalityIdValue) {
    const selectedMunicipality = municipalities.find((item) => String(item.id) === municipalityIdValue);
    onChange({
      ...form,
      municipalityId: municipalityIdValue,
      municipality: selectedMunicipality ? selectedMunicipality.name : '',
    });
  }

  function handleCeeToggle(value) {
    onChange({ ...form, hasCee: value, cee: value ? form.cee : '' });
  }

  function handleCeeChange(ceeValue) {
    onChange({ ...form, cee: ceeValue });
  }

  return (
    <form onSubmit={onSubmit} className="auth-form two-col">
      {FIELDS.slice(0, 6).map(([key, label, props]) => (
        <div key={key} className="form-field">
          <label>{label}</label>
          <input
            {...props}
            value={form[key]}
            onChange={e => handleChange(key, e.target.value)}
            required
          />
        </div>
      ))}

      <div className="form-field">
        <label>Estado</label>
        <select
          value={form.stateId || ''}
          onChange={e => handleStateChange(e.target.value)}
          disabled={catalogLoading}
          required
        >
          <option value="">{catalogLoading ? 'Cargando estados...' : 'Selecciona un estado'}</option>
          {states.map((state) => (
            <option key={state.id} value={state.id}>{state.name}</option>
          ))}
        </select>
      </div>

      <div className="form-field">
        <label>Municipio</label>
        <select
          value={form.municipalityId || ''}
          onChange={e => handleMunicipalityChange(e.target.value)}
          disabled={!form.stateId || municipalitiesLoading}
          required
        >
          <option value="">
            {form.stateId
              ? (municipalitiesLoading ? 'Cargando municipios...' : 'Selecciona un municipio')
              : 'Primero selecciona un estado'}
          </option>
          {municipalities.map((municipality) => (
            <option key={municipality.id} value={municipality.id}>{municipality.name}</option>
          ))}
        </select>
      </div>

      {FIELDS.slice(6).map(([key, label, props]) => (
        <div key={key} className="form-field">
          <label>{label}</label>
          <input
            {...props}
            value={form[key]}
            onChange={e => handleChange(key, e.target.value)}
            required
          />
        </div>
      ))}

      {(captchaEnabled || captchaLoading) && (
        <div className="form-field span2">
          <label>Captcha</label>
          {captchaEnabled ? (
          <div className="captcha-inline">
            <HCaptcha
              key={`hcaptcha-${captchaResetCounter}`}
              sitekey={hcaptchaSiteKey}
              onVerify={onCaptchaVerify}
              onExpire={onCaptchaExpire}
              onError={onCaptchaError}
            />
            <button
              type="button"
              className="btn-ghost btn-captcha-refresh"
              onClick={onRefreshCaptcha}
            >
              Recargar
            </button>
          </div>
          ) : (
            <p className="hint">Cargando hCaptcha...</p>
          )}
        </div>
      )}

      <div className="form-field">
        <label className="checkbox-label">
          <input type="checkbox" checked={Boolean(form.hasCee)} onChange={e => handleCeeToggle(e.target.checked)} />
          <span>Soy de un CEE (Comité Ejecutivo Estatal)</span>
        </label>
      </div>

      {form.hasCee && (
        <div className="form-field">
          <label>Selecciona CEE</label>
          <select value={form.cee || ''} onChange={e => handleCeeChange(e.target.value)} required={cees.length > 0}>
            <option value="">{cees.length > 0 ? 'Selecciona CEE' : 'No hay CEEs en catálogo'}</option>
            {cees.map((c) => (
              <option key={c.id ?? c.name} value={String(c.id ?? c.name)}>{c.name ?? c.nombre ?? c.label ?? String(c)}</option>
            ))}
          </select>
        </div>
      )}

      <div className="form-field span2">
        <label>Contraseña</label>
        <div className="input-eye">
          <input
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={e => handleChange('password', e.target.value)}
            minLength={10}
            required
          />
          <button
            type="button"
            className="eye-btn"
            onClick={onTogglePassword}
            aria-label={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
          >
            <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} aria-hidden="true" />
          </button>
        </div>
        <p className="hint">Minimo 10 caracteres, incluyendo mayuscula, minuscula, numero y simbolo.</p>
      </div>

      <div className="form-field span2">
        <label>Confirmar contraseña</label>
        <div className="input-eye">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={form.confirmPassword || ''}
            onChange={e => handleChange('confirmPassword', e.target.value)}
            required
          />
          <button
            type="button"
            className="eye-btn"
            onClick={onToggleConfirmPassword}
            aria-label={showConfirmPassword ? 'Ocultar confirmacion de contrasena' : 'Mostrar confirmacion de contrasena'}
          >
            <i className={showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} aria-hidden="true" />
          </button>
        </div>
      </div>
      <button type="submit" className="btn-primary full span2">Crear cuenta</button>
    </form>
  );
}
